import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AtSign, Loader2, CheckCircle2, XCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChangeUsernameCard() {
  const { user, refresh } = useAuth();
  const [next, setNext] = useState("");
  const [availability, setAvailability] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!next || next === user?.username) {
      setAvailability(null);
      return;
    }
    setAvailability("checking");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/check-username", { params: { username: next } });
        setAvailability(data);
      } catch {
        setAvailability(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [next, user?.username]);

  const changeNow = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      await api.post("/auth/change-username", { username: next.trim() });
      toast.success("Username updated");
      setNext("");
      setAvailability(null);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  const disabled = busy || !next || next === user?.username || (availability && availability !== "checking" && !availability.available);

  return (
    <Card className="rounded-3xl border-slate-100 p-6 shadow-sm" data-testid="settings-username-card">
      <div className="mb-2 flex items-center gap-2">
        <AtSign className="h-4 w-4 text-slate-400" />
        <h2 className="font-heading text-lg font-bold text-slate-900">Username</h2>
      </div>
      <p className="mb-4 text-sm text-slate-500">
        Your @username is your only identity on Yaprio. Your profile URL will change to
        <span className="mx-1 rounded bg-slate-50 px-1.5 py-0.5 font-mono text-xs text-slate-700">
          /u/&lt;new-username&gt;
        </span>
        and you can change it again in 7 days.
      </p>
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold text-slate-600">New username</Label>
        <div className="relative">
          <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder={user?.username || "newusername"}
            data-testid="input-change-username"
            className="pl-9 pr-9 h-11 rounded-xl"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            {availability === "checking" && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            {availability && availability !== "checking" && availability.available && (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            )}
            {availability && availability !== "checking" && !availability.available && (
              <XCircle className="h-4 w-4 text-red-500" />
            )}
          </span>
        </div>
        {availability && availability !== "checking" && (
          <p className={`text-xs font-medium ${availability.available ? "text-green-600" : "text-red-500"}`}>
            {availability.available ? "Available!" : availability.reason}
          </p>
        )}
      </div>
      <div className="mt-4">
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={disabled}
          data-testid="btn-change-username"
          className="rounded-full bg-pink-500 font-semibold text-white hover:bg-pink-600"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change username"}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-3xl" data-testid="change-username-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading font-extrabold">Change your username?</AlertDialogTitle>
            <AlertDialogDescription>
              Your profile URL will change to <span className="font-mono">/u/{next}</span>. Your existing chats,
              friends, and messages stay with you. You won't be able to change it again for 7 days.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={changeNow}
              data-testid="btn-confirm-change-username"
              className="rounded-full bg-pink-500 hover:bg-pink-600"
            >
              Yes, change it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
